import React, { useState } from 'react';

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  consent?: string;
}

// Add state for submission status
type SubmissionStatus = 'idle' | 'loading' | 'success' | 'error';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    message: '',
    consent: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<SubmissionStatus>('idle'); // Add status state
  const [submitMessage, setSubmitMessage] = useState<string>(''); // Message for success/error
  const [submitAttemptFailed, setSubmitAttemptFailed] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
    // Reset status if user starts typing again after an error/success
    if (status === 'success' || status === 'error') {
        setStatus('idle');
        setSubmitMessage('');
    }
    setSubmitAttemptFailed(false); // Reset on any change
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter your name.';
    if (!formData.email.trim()) newErrors.email = 'Please enter your email address so we can get back to you.';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address so we can get back to you.';
    if (formData.phone.trim() && !/^\+?[0-9\s]{7,}$/.test(formData.phone)) newErrors.phone = 'Please enter a valid phone number.';
    if (!formData.message.trim()) newErrors.message = 'Please enter your message.';
    if (!formData.consent) newErrors.consent = 'Please consent to the use of your data.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => { // Make handleSubmit async
    e.preventDefault();
    setSubmitMessage(''); // Clear previous messages
    setSubmitAttemptFailed(false); // Reset on new attempt

    if (!validateForm()) {
      console.log('Validation failed:', errors);
      setStatus('idle'); // Remain idle if validation fails
      setSubmitAttemptFailed(true); // Mark as failed
      return;
    }

    setStatus('loading'); // Set status to loading

    try {
      const response = await fetch('/api/contact', { // Your backend endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json(); // Assuming backend sends JSON response

      if (response.ok) {
        console.log('Form submitted successfully:', result);
        setStatus('success');
        setSubmitMessage('Thank you! Your message has been sent successfully.');
        // Reset form fields to initial state
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          role: '',
          message: '',
          consent: false, // Reset consent checkbox as well
        });
        setErrors({}); // Clear any previous validation errors
      } else {
        console.error('Submission failed:', result);
        setStatus('error');
        setSubmitMessage(result.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Network error:', error);
      setStatus('error');
      setSubmitMessage('A network error occurred. Please check your connection and try again.');
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="form-group">
        <label htmlFor="name">Name *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="John Smith"
          required
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? "name-error" : undefined}
          disabled={status === 'loading'} // Disable during loading
        />
        {errors.name && <span id="name-error" className="error-tooltip">{errors.name}</span>}
      </div>
      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="john.smith@example.com"
          required
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "email-error" : undefined}
          disabled={status === 'loading'} // Disable during loading
        />
        {errors.email && <span id="email-error" className="error-tooltip">{errors.email}</span>}
      </div>
      <div className="form-group">
        <label htmlFor="phone">Phone</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+44 7123 456789"
          aria-invalid={!!errors.phone} // Add accessibility attribute
          aria-describedby={errors.phone ? "phone-error" : undefined}
          disabled={status === 'loading'} // Disable during loading
        />
        {/* Add tooltip for phone error */}
        {errors.phone && <span id="phone-error" className="error-tooltip">{errors.phone}</span>}
      </div>
      <div className="form-group">
        <label htmlFor="company">Company</label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="Acme Corporation"
          disabled={status === 'loading'} // Disable during loading
        />
      </div>
      <div className="form-group"> {/* Changed class to className */}
        <label htmlFor="role">Role</label>
        <input
          type="text"
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          placeholder="Operations Director"
          disabled={status === 'loading'} // Disable during loading
        />
      </div>
      <div className="form-group">
        <label htmlFor="message">Message *</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={5}
          placeholder="I'm interested in discussing how IntelPro can help us..."
          required
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "message-error" : undefined}
          disabled={status === 'loading'} // Disable during loading
        ></textarea>
        {errors.message && <span id="message-error" className="error-tooltip">{errors.message}</span>}
      </div>
      <div className="form-group">
        <label className="consent-label"> {/* Added class for easier targeting */}
          <input
            type="checkbox"
            name="consent"
            checked={formData.consent}
            onChange={handleChange}
            required
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? "consent-error" : undefined}
            disabled={status === 'loading'} // Disable during loading
          />
          I consent to the use of my data for this enquiry *
        </label>
        {errors.consent && <span id="consent-error" className="error-tooltip consent-error-tooltip">{errors.consent}</span>} {/* Added class for specific styling */}
      </div>
      <div className="form-note">* Required Fields</div>

      {/* Display Submission Status Message */}
      {submitMessage && (
        <div className={`submit-message ${status === 'success' ? 'success' : 'error'}`}>
          {submitMessage}
        </div>
      )}

      <button
        type="submit"
        className={`submit-button${submitAttemptFailed ? ' submit-button--invalid-attempt' : ''}`}
        disabled={status === 'loading'}
      >
        {status === 'loading' ? 'Sending...' : 'Submit'}
      </button>
    </form>
  );
};

export default ContactForm;